import { create } from "zustand";

import type { SelectedWriter, Writer } from "../types/writer";

// Define the store state
type WriterStoreState = {
  selectedWriters: SelectedWriter[];
  availableWriters: Writer[];
  isLoading: boolean;
  error: string | null;
};

// Define the store actions
type WriterStoreActions = {
  setSelectedWriters: (writers: SelectedWriter[]) => void;
  addSelectedWriter: (writer: SelectedWriter) => void;
  removeSelectedWriter: (writercode: string) => void;
  updateSelectedWriter: (writercode: string, updatedWriter: SelectedWriter) => void;
  setAvailableWriters: (writers: Writer[]) => void;
  updateAvailableWriter: (writercode: string, updatedWriter: Writer) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearSelectedWriters: () => void;
  clearState: () => void;
};

// Create the store
const useWriterStore = create<WriterStoreState & WriterStoreActions>((set, get) => ({
  selectedWriters: [],
  availableWriters: [],
  isLoading: false,
  error: null,

  setSelectedWriters: selectedWriters => set({ selectedWriters }),

  addSelectedWriter: (writer) => {
    const { selectedWriters } = get();
    // Check if writer is already selected
    if (!selectedWriters.some(w => w.writercode === writer.writercode)) {
      set({ selectedWriters: [...selectedWriters, writer] });
    }
  },

  removeSelectedWriter: (writercode) => {
    const { selectedWriters } = get();
    set({
      selectedWriters: selectedWriters.filter(w => w.writercode !== writercode),
    });
  },

  updateSelectedWriter: (writercode, updatedWriter) => {
    const { selectedWriters } = get();
    set({
      selectedWriters: selectedWriters.map(w =>
        w.writercode === writercode ? updatedWriter : w,
      ),
    });
  },

  setAvailableWriters: availableWriters => set({ availableWriters }),

  updateAvailableWriter: (writercode, updatedWriter) => {
    const { availableWriters } = get();
    set({
      availableWriters: availableWriters.map(w =>
        w.writercode === writercode ? updatedWriter : w,
      ),
    });
  },

  setIsLoading: isLoading => set({ isLoading }),

  setError: error => set({ error }),

  clearSelectedWriters: () => set({ selectedWriters: [] }),

  clearState: () => set({
    selectedWriters: [],
    availableWriters: [],
    isLoading: false,
    error: null,
  }),
}));

export default useWriterStore;
